import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/layout/SiteChrome";

const steps = [
  {
    n: "01", title: "Select Your Device",
    desc: "Choose from mobiles, tablets, or laptops. Pick your brand and exact model from our extensive database of thousands of devices.",
    details: ["Browse by category or search directly", "Select your exact model and storage variant", "Takes less than 1 minute"],
  },
  {
    n: "02", title: "Answer a Few Questions",
    desc: "Tell us about the condition of your device — screen, battery, physical body, and whether you have the accessories.",
    details: ["Screen condition (flawless, scratches, cracked)", "Battery health percentage", "Physical damage assessment", "Accessories availability"],
  },
  {
    n: "03", title: "Get Your Instant Quote",
    desc: "See your guaranteed price instantly — no negotiations, no hidden deductions. The price we show is the price you get.",
    details: ["Price valid for 24 hours", "Fully transparent breakdown", "No surprise deductions"],
  },
  {
    n: "04", title: "Free Doorstep Pickup",
    desc: "Accept the quote and our certified technician will come to your home or office at your preferred time — absolutely free.",
    details: ["Available 7 days a week, 9AM–8PM", "Certified technicians only", "Quick 10-minute inspection"],
  },
  {
    n: "05", title: "Instant Bank Transfer",
    desc: "Once inspection is done and everything matches, money is instantly transferred to your bank account — right there on the spot.",
    details: ["IMPS / UPI transfer in seconds", "No waiting, no cheques", "Get notified via SMS"],
  },
  {
    n: "06", title: "Done — Enjoy Your Cash!",
    desc: "That's it! You've successfully sold your device. Thank you for choosing True Value Mobiles.",
    details: ["Sell again anytime", "Refer friends to earn", "Top-notch support"],
  },
];

const faqs = [
  { q: "How is the price determined?", a: "Our price is based on the current resale market value of your device, adjusted for its condition. We use real-time data to ensure you always get the best possible price." },
  { q: "Is the price guaranteed?", a: "Yes! The price shown is guaranteed as long as the device condition matches what you described. If there are undisclosed issues found during inspection, we may revise the price — but you can always decline." },
  { q: "How long does the pickup take?", a: "Our technician will contact you within 2 hours of accepting the quote. We typically complete pickups within 24 hours, 7 days a week." },
  { q: "What if my device condition differs from what I selected?", a: "Our technician will re-assess on the spot. If the revised price doesn't suit you, you can simply keep your device — no pressure, no fees." },
  { q: "Is my data safe?", a: "Absolutely. We factory reset all devices before processing. You should also wipe your device before the pickup. Your personal data is never accessed or stored by us." },
  { q: "What devices do you accept?", a: "We accept all major brands — Apple, Samsung, OnePlus, Xiaomi, Vivo, Oppo and more. Mobiles, tablets, and laptops are all supported." },
];

export default function HowItWorksPage() {
  const [catId, setCatId] = React.useState("");

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/categories`)
      .then(r => r.json())
      .then(data => {
        const cats = data.categories || [];
        const m = cats.find((c: any) => c.name.toLowerCase().includes("phone") || c.name.toLowerCase().includes("mobile"));
        if (m) setCatId(m._id);
        else if (cats.length > 0) setCatId(cats[0]._id);
      })
      .catch(console.error);
  }, []);

  const sellTo = catId ? `/category/${catId}` : "/";

  return (
    <div className="page-bg">
      <Header />

      {/* Hero */}
      <section style={{ background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%)", padding: "clamp(36px,7vw,56px) 20px clamp(32px,7vw,52px)" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "rgba(255,255,255,0.65)", textDecoration: "none", marginBottom: 20, transition: "color 0.2s" }}
            onMouseEnter={e => (e.currentTarget.style.color = "#fff")} onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.65)")}>
            ← Back to Home
          </Link>
          <div style={{ maxWidth: 600 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 10 }}>SIMPLE PROCESS</p>
            <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(28px,4vw,44px)", color: "#fff", letterSpacing: "-1px", marginBottom: 14 }}>
              How It Works
            </h1>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,0.85)", lineHeight: 1.7 }}>
              Selling your old device has never been easier. From quote to cash in hand — we handle everything. Here's exactly how it works.
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "64px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {steps.map((s, i) => (
            <div key={s.n} className="animate-fade-up" style={{ animationDelay: `${i * 80}ms`, display: "flex", gap: 0, position: "relative" }}>
              {i < steps.length - 1 && (
                <div className="hide-mobile" style={{ position: "absolute", left: 23, top: 56, bottom: -24, width: 2, background: "linear-gradient(180deg, var(--brand) 0%, var(--border) 100%)", zIndex: 0 }} />
              )}
              <div className="hide-mobile" style={{ flexShrink: 0, width: 48, height: 48, borderRadius: "50%", background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, zIndex: 1, boxShadow: "0 4px 14px rgba(255,107,53,0.35)", marginRight: 24 }}>
                {i + 1}
              </div>
              <div style={{ flex: 1, background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 18, padding: "clamp(16px,3vw,24px)", marginBottom: 18, boxShadow: "var(--shadow-sm)", transition: "all 0.25s" }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--border-bright)"; el.style.boxShadow = "var(--shadow-lg)"; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--border)"; el.style.boxShadow = "var(--shadow-sm)"; }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--brand)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>STEP {s.n}</div>
                    <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: "var(--text)", marginBottom: 8 }}>{s.title}</h3>
                    <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.65, marginBottom: 14 }}>{s.desc}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {s.details.map((d, j) => (
                        <div key={j} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--brand-light)", borderRadius: 100, padding: "4px 12px", fontSize: 12, fontWeight: 500, color: "var(--brand-dark)" }}>
                          <span style={{ fontSize: 10 }}>✓</span> {d}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", padding: "64px 20px" }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", color: "var(--brand)", textTransform: "uppercase", marginBottom: 8 }}>GOT QUESTIONS?</p>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(22px,4vw,34px)", color: "var(--text)", letterSpacing: "-0.5px" }}>Frequently Asked Questions</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {faqs.map((faq, i) => <FaqItem key={i} faq={faq} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 1160, margin: "0 auto", padding: "64px 20px" }}>
        <div style={{
          background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%)",
          borderRadius: 22, padding: "clamp(32px,6vw,48px) clamp(20px,4vw,32px)", textAlign: "center",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }} />
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(22px,4vw,32px)", color: "#fff", marginBottom: 12, position: "relative" }}>Ready to get started?</h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.85)", marginBottom: 26, position: "relative" }}>Get an instant quote for your device in just 1 minute.</p>
          <Link to={sellTo} style={{
            display: "inline-block", background: "#fff", color: "var(--brand-dark)", borderRadius: 12,
            padding: "13px 30px", fontWeight: 700, fontSize: 15, fontFamily: "var(--font-display)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)", transition: "all 0.2s", position: "relative",
          }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.transform = ""; }}>
            Sell My Device →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FaqItem({ faq }: { faq: { q: string; a: string } }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 14, overflow: "hidden", transition: "all 0.2s" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", background: "none", border: "none", cursor: "pointer", gap: 12 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15, color: "var(--text)", textAlign: "left" }}>{faq.q}</span>
        <span style={{ fontSize: 18, color: "var(--brand)", transition: "transform 0.25s", transform: open ? "rotate(45deg)" : "none", flexShrink: 0 }}>+</span>
      </button>
      {open && (
        <div style={{ padding: "0 20px 18px", fontSize: 14, color: "var(--text2)", lineHeight: 1.7, animation: "fadeUp 0.2s ease" }}>
          {faq.a}
        </div>
      )}
    </div>
  );
}
